import React, { useEffect, useState, useCallback } from 'react';
import { X, Home, Loader2, AlertCircle, MapPin, FileText, Building2, Plus, Trash2, Mail, Paperclip, MessageSquare, Users, ChevronDown, ChevronRight } from 'lucide-react';
import EntityLink from '../shared/EntityLink';
import { supabase } from '../../supabaseClient';
import { EditableCell } from '../EditableCell';
import { useAuth } from '../../hooks/useAuth';
import { formatDocument } from '../../utils/formatDocument';
import IntakeZone from '../intake/IntakeZone';
import KnowledgeSection from '../shared/KnowledgeSection';

const TIPO_USO_OPTIONS = [
  { value: '',              label: '—' },
  { value: 'residential',   label: 'Residencial' },
  { value: 'commercial',    label: 'Comercial' },
  { value: 'mixed',         label: 'Misto' },
  { value: 'industrial',    label: 'Industrial' },
  { value: 'vacant',        label: 'Vago' },
  { value: 'rural',         label: 'Rural' },
  { value: 'institutional', label: 'Institucional' },
];
const SITUACAO_OPTIONS = [
  { value: 'active',          label: 'Ativo' },
  { value: 'in_negotiation',  label: 'Em negociação' },
  { value: 'sold',            label: 'Vendido' },
  { value: 'expropriated',    label: 'Desapropriado' },
  { value: 'judicial',        label: 'Judicial' },
  { value: 'archived',        label: 'Arquivado' },
];
const TIPO_PROPRIEDADE_OPTIONS = [
  { value: 'full_ownership', label: 'Plena' },
  { value: 'bare_ownership', label: 'Nua propriedade' },
  { value: 'usufruct',       label: 'Usufruto' },
  { value: 'possession',     label: 'Posse' },
  { value: 'judicial',       label: 'Judicial' },
];

const fmtMoney = (v) => {
  if (v == null) return '—';
  const n = Number(v);
  if (!isFinite(n) || n === 0) return '—';
  return `R$ ${n.toLocaleString('pt-BR')}`;
};

/**
 * Ficha completa do imovel (Modulo Imoveis Fase C — Bloco 2).
 * Seções:
 *  1. Header (nome + situação editável + endereço + m²)
 *  2. Dados cadastrais (matrícula, CRI, inscrição municipal, tipo_uso, valor_venal, valor_referencia)
 *  3. Lote (read-only; editar no lote = outro modal)
 *  4. Proprietários (lista editável: %, tipo)
 *  5. Áreas que contêm este imóvel
 *  6. Propostas recebidas para este imóvel
 *
 * Anexos ficam para Fase D (email_attachments não tem FK dedicada para imovel_id
 * — extensão de schema futura).
 */
// 06/mai/26: ordem de exibição dos proprietários por ownership_status
//   (deceased primeiro, original no meio, heir por último).
const OWNERSHIP_STATUS_ORDER = { deceased: 0, original: 1, heir: 2 };

const ImovelDetailModal = ({ imovelId, onClose }) => {
  const [imovel, setImovel] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  // Frente C cascata_por_oferta_proprietarios 2026-04-26: CRUD proprietarios inline
  const [addingProp, setAddingProp] = useState(false);
  // 06/mai/26: proprietarios via v_proprietarios (inclui ownership_status computed)
  const [proprietarios, setProprietarios] = useState([]);
  // Onda 11 abr/26: contatos vinculados via v_entity_contacts
  const [linkedContacts, setLinkedContacts] = useState([]);

  const fetchAll = useCallback(async () => {
    if (!imovelId) return;
    setLoading(true); setError(null);
    try {
      const { data, error: qErr } = await supabase
        .from('imoveis')
        .select(`
          *,
          lote:lotes(id, nome, zoneamento, area_total_m2, endereco, bairro),
          areas_link:area_imoveis(
            id, ordem,
            area:areas(id, nome)
          ),
          propostas_link:proposta_imoveis(
            id, valor_especifico, area_especifica_m2,
            proposta:propostas(
              id, nome, tipo, valor_proposto, valor_permuta, status, data_proposta,
              proponente:empresas!propostas_proponente_empresa_id_fkey(id, empresa)
            )
          )
        `)
        .eq('id', imovelId)
        .single();
      if (qErr) throw qErr;
      setImovel(data);

      // Proprietários da view (traz notas + ownership_status computed). Ordenação:
      //   deceased → original → heir, e dentro de cada grupo percentual DESC.
      const { data: propsData, error: propsErr } = await supabase
        .from('v_proprietarios')
        .select('*')
        .eq('imovel_id', imovelId);
      if (propsErr) throw propsErr;
      const sorted = (propsData || []).slice().sort((a, b) => {
        const sa = OWNERSHIP_STATUS_ORDER[a.ownership_status] ?? 99;
        const sb = OWNERSHIP_STATUS_ORDER[b.ownership_status] ?? 99;
        if (sa !== sb) return sa - sb;
        return Number(b.percentual_participacao || 0) - Number(a.percentual_participacao || 0);
      });
      setProprietarios(sorted);

      const { data: lc } = await supabase
        .from('v_entity_contacts')
        .select('*')
        .eq('entity_type', 'imovel')
        .eq('entity_id', imovelId);
      setLinkedContacts(lc || []);
    } catch (err) {
      console.error('[ImovelDetailModal] fetch falhou:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [imovelId]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const saveField = async (campo, valor) => {
    if (!imovelId) return;
    try {
      const { error: upErr } = await supabase
        .from('imoveis')
        .update({ [campo]: valor, updated_at: new Date().toISOString() })
        .eq('id', imovelId);
      if (upErr) throw upErr;
      await fetchAll();
    } catch (err) {
      setError(err.message);
    }
  };

  const saveProprietarioField = async (proprietarioId, campo, valor) => {
    try {
      const { error: upErr } = await supabase
        .from('imovel_proprietarios')
        .update({ [campo]: valor, updated_at: new Date().toISOString() })
        .eq('id', proprietarioId);
      if (upErr) throw upErr;
      await fetchAll();
    } catch (err) {
      setError(err.message);
    }
  };

  const deleteProprietario = async (proprietarioId) => {
    if (!window.confirm('Remover este proprietário do imóvel? O registro histórico do vínculo será apagado (não afeta a empresa/contato nem outros imóveis).')) return;
    try {
      const { error: delErr } = await supabase
        .from('imovel_proprietarios')
        .delete()
        .eq('id', proprietarioId);
      if (delErr) throw delErr;
      await fetchAll();
    } catch (err) {
      setError(err.message);
    }
  };

  const handleAddProprietario = async (payload) => {
    try {
      const { error: insErr } = await supabase
        .from('imovel_proprietarios')
        .insert({ ...payload, imovel_id: imovelId });
      if (insErr) throw insErr;
      setAddingProp(false);
      await fetchAll();
    } catch (err) {
      setError(err.message);
    }
  };

  const sitLabel = imovel?.situacao
    ? SITUACAO_OPTIONS.find(o => o.value === imovel.situacao)?.label || imovel.situacao
    : '—';
  const tipoUsoLabel = imovel?.tipo_uso_atual
    ? TIPO_USO_OPTIONS.find(o => o.value === imovel.tipo_uso_atual)?.label || imovel.tipo_uso_atual
    : '—';

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-[10015] p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-[95vw] 2xl:max-w-7xl max-h-[95vh] flex flex-col">
        {/* Header */}
        <div className="p-4 border-b border-slate-200 flex items-start justify-between gap-3">
          <div className="min-w-0 flex-grow">
            <div className="flex items-center gap-2 mb-1">
              <Home size={18} className="text-slate-600" />
              <h2 className="text-lg font-bold text-slate-800 truncate">
                {imovel?.nome || (loading ? 'Carregando...' : '—')}
              </h2>
              {imovel && (
                <EditableCell
                  value={imovel.situacao || ''}
                  options={SITUACAO_OPTIONS}
                  onSave={(v) => saveField('situacao', v || null)}
                  type="select"
                  displayValue={sitLabel}
                  colorClass="text-[11px] px-2 py-0.5 rounded bg-amber-100 text-amber-800"
                />
              )}
            </div>
            {imovel && (
              <div className="text-xs text-slate-500">
                {[imovel.endereco, imovel.numero].filter(Boolean).join(' ')}
                {imovel.bairro ? ` · ${imovel.bairro}` : ''}
                {imovel.area_terreno_m2 ? ` · ${Number(imovel.area_terreno_m2).toLocaleString('pt-BR')}m²` : ''}
              </div>
            )}
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600 flex-shrink-0">
            <X size={20} />
          </button>
        </div>

        <div className="flex-grow overflow-y-auto p-4 space-y-4">
          {loading && (
            <div className="flex items-center gap-2 text-sm text-slate-500 py-6 justify-center">
              <Loader2 size={16} className="animate-spin" /> Carregando ficha...
            </div>
          )}
          {error && (
            <div className="flex items-start gap-2 p-2 bg-red-50 border border-red-200 rounded text-xs text-red-800">
              <AlertCircle size={12} className="flex-shrink-0 mt-0.5" /> {error}
            </div>
          )}

          {imovel && !loading && (
            <>
              {/* Onda 6/7/9A abr/26: intake de documento via IntakeZone universal */}
              <IntakeZone
                entityType="imovel"
                entityId={imovelId}
                defaultDocType="matricula_imovel"
                onComplete={fetchAll}
              />

              {/* Dados cadastrais */}
              <section>
                <div className="text-[10px] uppercase tracking-wider font-semibold text-slate-500 mb-2">
                  Dados cadastrais
                </div>
                <div className="grid grid-cols-2 gap-x-4 gap-y-2">
                  <FieldRow label="Nome" value={imovel.nome}
                    onSave={(v) => saveField('nome', v || imovel.nome)} type="text" />
                  <FieldRow label="Matrícula" value={imovel.matricula}
                    onSave={(v) => saveField('matricula', v || null)} type="text" />
                  <FieldRow label="CRI" value={imovel.cri}
                    onSave={(v) => saveField('cri', v || null)} type="text" />
                  <FieldRow label="Inscrição Municipal" value={imovel.inscricao_municipal}
                    onSave={(v) => saveField('inscricao_municipal', v || null)} type="text" />
                  <FieldRow label="Tipo de uso" value={imovel.tipo_uso_atual}
                    onSave={(v) => saveField('tipo_uso_atual', v || null)} type="select"
                    options={TIPO_USO_OPTIONS} displayValue={tipoUsoLabel} />
                  <FieldRow label="Área terreno (m²)" value={imovel.area_terreno_m2}
                    onSave={(v) => saveField('area_terreno_m2', parseNumOrNull(v))} type="text"
                    displayValue={imovel.area_terreno_m2 ? Number(imovel.area_terreno_m2).toLocaleString('pt-BR') : '—'} />
                  <FieldRow label="Área construída (m²)" value={imovel.area_construida_m2}
                    onSave={(v) => saveField('area_construida_m2', parseNumOrNull(v))} type="text"
                    displayValue={imovel.area_construida_m2 ? Number(imovel.area_construida_m2).toLocaleString('pt-BR') : '—'} />
                  <FieldRow label="Valor venal" value={imovel.valor_venal}
                    onSave={(v) => saveField('valor_venal', parseMoneyOrNull(v))} type="text"
                    displayValue={fmtMoney(imovel.valor_venal)} />
                  <FieldRow label="Valor referência" value={imovel.valor_referencia}
                    onSave={(v) => saveField('valor_referencia', parseMoneyOrNull(v))} type="text"
                    displayValue={fmtMoney(imovel.valor_referencia)} />
                </div>
                <div className="mt-2">
                  <div className="text-[10px] text-slate-500 mb-0.5">Notas</div>
                  <EditableCell
                    value={imovel.notas || ''}
                    onSave={(v) => saveField('notas', v || null)}
                    type="textarea"
                    placeholder="Clique para adicionar notas..."
                    className="text-xs text-slate-700 block w-full"
                  />
                </div>
              </section>

              {/* Lote */}
              {imovel.lote && (
                <section className="bg-slate-50 rounded border border-slate-200 p-3">
                  <div className="text-[10px] uppercase tracking-wider font-semibold text-slate-500 mb-1">
                    Lote (superfície registrada)
                  </div>
                  <div className="text-sm text-slate-700 font-medium">{imovel.lote.nome}</div>
                  <div className="text-xs text-slate-500 mt-0.5">
                    {imovel.lote.zoneamento && `Zoneamento: ${imovel.lote.zoneamento}`}
                    {imovel.lote.area_total_m2 ? ` · ${Number(imovel.lote.area_total_m2).toLocaleString('pt-BR')}m² totais` : ''}
                    {imovel.lote.bairro ? ` · ${imovel.lote.bairro}` : ''}
                  </div>
                </section>
              )}

              {/* Proprietarios (Frente C cascata_por_oferta_proprietarios 2026-04-26: CRUD inline) */}
              {/* 06/mai/26: dados via v_proprietarios — traz notas + ownership_status para destaque visual de falecido(a)/herdeiro(a). */}
              <section>
                <div className="text-[10px] uppercase tracking-wider font-semibold text-slate-500 mb-2 flex items-center justify-between">
                  <span className="flex items-center gap-1">
                    👤 Proprietários ({proprietarios.length})
                  </span>
                  {!addingProp && (
                    <button
                      onClick={() => setAddingProp(true)}
                      className="text-[10px] px-1.5 py-0.5 bg-slate-700 text-white rounded hover:bg-slate-800 flex items-center gap-0.5 normal-case tracking-normal"
                    >
                      <Plus size={9} /> adicionar
                    </button>
                  )}
                </div>

                {proprietarios.length === 0 && !addingProp && (
                  <div className="text-xs text-slate-400 italic">Nenhum proprietário vinculado.</div>
                )}

                {proprietarios.length > 0 && (
                  <div className="space-y-1.5">
                    {proprietarios.map(p => (
                      <ProprietarioRow
                        key={p.proprietario_id}
                        proprietario={p}
                        onUpdate={(field, value) => saveProprietarioField(p.proprietario_id, field, value)}
                        onDelete={() => deleteProprietario(p.proprietario_id)}
                      />
                    ))}
                  </div>
                )}

                {addingProp && (
                  <div className="mt-1.5">
                    <AddProprietarioForm
                      onSave={handleAddProprietario}
                      onCancel={() => setAddingProp(false)}
                    />
                  </div>
                )}
              </section>

              {/* Onda 11 abr/26: contatos vinculados via v_entity_contacts */}
              {linkedContacts.length > 0 && (
                <section>
                  <div className="text-[10px] uppercase tracking-wider font-semibold text-slate-500 mb-2 flex items-center gap-1">
                    🔗 Contatos vinculados ({linkedContacts.length})
                  </div>
                  {linkedContacts.map((lc, i) => (
                    <div key={`${lc.contato_id}-${lc.role}-${i}`} className="text-xs py-1 border-b border-slate-100 flex items-center gap-2 flex-wrap">
                      <EntityLink entityType="contato" entityId={lc.contato_id} label={lc.contato_nome || '—'} />
                      <span className="px-1.5 py-0.5 text-[9px] bg-purple-100 text-purple-700 rounded">{lc.role_label}</span>
                      {lc.is_primary && <span className="text-[8px] text-amber-600">★</span>}
                      {lc.eh_decisor && <span className="text-[9px]">⭐</span>}
                      {lc.notes && <span className="text-[10px] text-slate-400 truncate ml-1" title={lc.notes}>{lc.notes.slice(0, 60)}</span>}
                    </div>
                  ))}
                </section>
              )}

              {/* UX issue 4 abr/26: ofertas/propostas via v_imovel_ofertas (empresa compradora + oferta + proposta) */}
              <ImovelOfertasSection imovelId={imovelId} />

              {/* K1 26/abr/26: Seção Conhecimento (knowledge_atoms via v_entity_knowledge) */}
              <KnowledgeSection entityType="imovel" entityId={imovelId} />

              {/* Onda 4 abr/26 — J1: emails vinculados */}
              <ImovelEmailsSection imovelId={imovelId} />

              {/* Onda 4 abr/26 — J2: anexos/documentos */}
              <ImovelAnexosSection imovelId={imovelId} />

              {/* Onda 4 abr/26 — J3: timeline imovel_context */}
              <ImovelTimelineSection imovelId={imovelId} />

              {/* Areas que contem este imovel */}
              {imovel.areas_link?.length > 0 && (
                <section>
                  <div className="text-[10px] uppercase tracking-wider font-semibold text-slate-500 mb-2 flex items-center gap-1">
                    <MapPin size={10} /> Áreas ({imovel.areas_link.length})
                  </div>
                  <div className="flex flex-wrap gap-1.5">
                    {imovel.areas_link.map(al => (
                      <span
                        key={al.id}
                        className="text-[11px] px-2 py-1 bg-emerald-50 border border-emerald-200 rounded"
                        title={al.ordem != null ? `Ordem: ${al.ordem}` : undefined}
                      >
                        {al.area?.nome || '—'}
                      </span>
                    ))}
                  </div>
                </section>
              )}

              {/* Propostas recebidas */}
              {imovel.propostas_link?.length > 0 && (
                <section>
                  <div className="text-[10px] uppercase tracking-wider font-semibold text-slate-500 mb-2 flex items-center gap-1">
                    <FileText size={10} /> Propostas recebidas ({imovel.propostas_link.length})
                  </div>
                  <div className="space-y-1.5">
                    {imovel.propostas_link.map(pl => {
                      const p = pl.proposta;
                      if (!p) return null;
                      return (
                        <div key={pl.id} className="bg-white border border-slate-200 rounded p-2 text-xs">
                          <div className="flex items-center justify-between gap-2">
                            <div className="font-medium text-slate-800 truncate">{p.nome}</div>
                            <div className="text-[10px] text-slate-500">{p.status}</div>
                          </div>
                          <div className="flex flex-wrap gap-2 text-[11px] text-slate-600 mt-1">
                            {p.valor_proposto && <span>💰 {fmtMoney(p.valor_proposto)}</span>}
                            {p.valor_permuta && <span>🔄 Permuta: {fmtMoney(p.valor_permuta)}</span>}
                            {p.proponente?.empresa && <span><Building2 size={10} className="inline" /> {p.proponente.empresa}</span>}
                            {p.data_proposta && <span>📅 {new Date(p.data_proposta).toLocaleDateString('pt-BR')}</span>}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </section>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

/**
 * Sub-componente FieldRow (compact 2-col label/value para EditableCell).
 */
const FieldRow = ({ label, value, onSave, type, options, displayValue }) => (
  <div>
    <div className="text-[10px] text-slate-500 mb-0.5">{label}</div>
    <EditableCell
      value={value != null ? String(value) : ''}
      onSave={onSave}
      type={type || 'text'}
      options={options}
      displayValue={displayValue != null ? displayValue : (value || '—')}
      className="text-xs block w-full"
    />
  </div>
);

const parseNumOrNull = (v) => {
  const raw = (v || '').replace(/[^\d.,-]/g, '').replace(',', '.');
  const n = raw ? parseFloat(raw) : NaN;
  return Number.isFinite(n) ? n : null;
};
const parseMoneyOrNull = parseNumOrNull;

/**
 * UX issue 4 abr/26 — ofertas/propostas envolvendo este imóvel.
 * View v_imovel_ofertas agrega empresa compradora + oferta + proposta.
 */
const ImovelOfertasSection = ({ imovelId }) => {
  const [rows, setRows] = useState(null);
  const [err, setErr] = useState(null);

  useEffect(() => {
    if (!imovelId) return;
    let cancel = false;
    (async () => {
      const { data, error } = await supabase
        .from('v_imovel_ofertas')
        .select('*')
        .eq('imovel_id', imovelId);
      if (cancel) return;
      if (error) setErr(error.message);
      else setRows(data || []);
    })();
    return () => { cancel = true; };
  }, [imovelId]);

  if (err) {
    return (
      <section>
        <div className="text-[11px] text-red-600 italic">Erro ao carregar ofertas: {err}</div>
      </section>
    );
  }
  if (rows === null) return null;

  return (
    <section>
      <div className="text-[10px] uppercase tracking-wider font-semibold text-slate-500 mb-2 flex items-center gap-1">
        📋 Ofertas/Propostas envolvendo este imóvel ({rows.length})
      </div>
      {rows.length === 0 ? (
        <div className="text-xs text-slate-400 italic">Nenhuma proposta vinculada a este imóvel.</div>
      ) : (
        <div className="space-y-1.5">
          {rows.map((r, idx) => (
            <div key={r.proposta_id || `${r.oferta_id}-${idx}`} className="bg-white border border-slate-200 rounded p-2 text-xs">
              <div className="flex items-center justify-between gap-2 mb-0.5">
                <div className="font-medium text-slate-800 truncate flex items-center gap-1">
                  <Building2 size={11} className="text-indigo-600 flex-shrink-0" />
                  {r.empresa_compradora || <span className="text-slate-400">— sem empresa —</span>}
                </div>
                {r.oferta_status && (
                  <span className="text-[10px] px-1.5 py-0.5 bg-orange-50 text-orange-700 border border-orange-200 rounded flex-shrink-0">
                    {r.oferta_status}
                  </span>
                )}
              </div>
              {r.oferta_nome && (
                <div className="text-[11px] text-slate-600 truncate" title={r.oferta_nome}>
                  Oferta: {r.oferta_nome}
                </div>
              )}
              <div className="flex flex-wrap gap-2 text-[11px] text-slate-600 mt-1">
                {r.proposta_nome && <span className="truncate max-w-[240px]" title={r.proposta_nome}>📄 {r.proposta_nome}</span>}
                {r.valor_proposto != null && <span>💰 {fmtMoney(r.valor_proposto)}</span>}
                {r.proposta_status && <span className="text-slate-500">· {r.proposta_status}</span>}
                {r.data_proposta && <span>📅 {new Date(r.data_proposta).toLocaleDateString('pt-BR')}</span>}
              </div>
            </div>
          ))}
        </div>
      )}
    </section>
  );
};

/**
 * Onda 4 abr/26 — J1: emails vinculados ao imóvel via email_participations.
 */
const ImovelEmailsSection = ({ imovelId }) => {
  const [rows, setRows] = useState(null);
  useEffect(() => {
    if (!imovelId) return;
    let cancel = false;
    (async () => {
      const { data } = await supabase
        .from('email_participations')
        .select('email_id, emails_gmail!inner(id, subject, from_email, data_envio)')
        .eq('entity_type', 'imovel')
        .eq('entity_id', imovelId)
        .order('created_at', { ascending: false })
        .limit(10);
      if (!cancel) setRows(data || []);
    })();
    return () => { cancel = true; };
  }, [imovelId]);
  if (rows === null) return null;
  return (
    <section>
      <div className="text-[10px] uppercase tracking-wider font-semibold text-slate-500 mb-2 flex items-center gap-1">
        <Mail size={10} /> Emails vinculados ({rows.length})
      </div>
      {rows.length === 0 ? (
        <div className="text-xs text-slate-400 italic">Nenhum email vinculado.</div>
      ) : (
        <div className="space-y-1">
          {rows.map((r) => {
            const e = r.emails_gmail;
            if (!e) return null;
            return (
              <div key={r.email_id} className="bg-white border border-slate-200 rounded p-2 text-xs flex items-start gap-2">
                <Mail size={11} className="text-slate-400 flex-shrink-0 mt-0.5" />
                <div className="min-w-0 flex-1">
                  <div className="font-medium text-slate-800 truncate" title={e.subject || ''}>{e.subject || '(sem assunto)'}</div>
                  <div className="text-[11px] text-slate-500 truncate">
                    {e.from_email || '—'} · {e.data_envio ? new Date(e.data_envio).toLocaleDateString('pt-BR') : '—'}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </section>
  );
};

/**
 * Onda 4 abr/26 — J2: documentos/anexos do imóvel.
 */
const ImovelAnexosSection = ({ imovelId }) => {
  const [rows, setRows] = useState(null);
  useEffect(() => {
    if (!imovelId) return;
    let cancel = false;
    (async () => {
      const { data } = await supabase
        .from('email_attachments')
        .select('id, filename, mime_type, document_type, size_bytes, created_at')
        .eq('imovel_id', imovelId)
        .order('created_at', { ascending: false });
      if (!cancel) setRows(data || []);
    })();
    return () => { cancel = true; };
  }, [imovelId]);
  if (rows === null) return null;
  const fmtSize = (b) => {
    if (b == null) return '';
    if (b < 1024) return `${b} B`;
    if (b < 1024 * 1024) return `${(b / 1024).toFixed(1)} KB`;
    return `${(b / (1024 * 1024)).toFixed(1)} MB`;
  };
  return (
    <section>
      <div className="text-[10px] uppercase tracking-wider font-semibold text-slate-500 mb-2 flex items-center gap-1">
        <Paperclip size={10} /> Documentos / Anexos ({rows.length})
      </div>
      {rows.length === 0 ? (
        <div className="text-xs text-slate-400 italic">Nenhum documento vinculado.</div>
      ) : (
        <div className="space-y-1">
          {rows.map((a) => (
            <div key={a.id} className="bg-white border border-slate-200 rounded p-2 text-xs flex items-center gap-2">
              <FileText size={11} className="text-slate-400 flex-shrink-0" />
              <div className="min-w-0 flex-1">
                <div className="font-medium text-slate-800 truncate" title={a.filename || ''}>{a.filename || '(sem nome)'}</div>
                <div className="text-[11px] text-slate-500 flex flex-wrap gap-2">
                  {a.document_type && <span className="px-1 bg-slate-100 rounded">{a.document_type}</span>}
                  {a.mime_type && <span className="text-slate-400">{a.mime_type}</span>}
                  {a.size_bytes && <span>{fmtSize(a.size_bytes)}</span>}
                  {a.created_at && <span>· {new Date(a.created_at).toLocaleDateString('pt-BR')}</span>}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </section>
  );
};

/**
 * Onda 4 abr/26 — J3: timeline imovel_context + adicionar nova nota.
 */
const ImovelTimelineSection = ({ imovelId }) => {
  const { user } = useAuth();
  const [rows, setRows] = useState(null);
  const [texto, setTexto] = useState('');
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    if (!imovelId) return;
    const { data } = await supabase
      .from('imovel_context')
      .select('*')
      .eq('imovel_id', imovelId)
      .order('created_at', { ascending: false });
    setRows(data || []);
  }, [imovelId]);

  useEffect(() => { load(); }, [load]);

  const submit = async () => {
    if (!texto.trim() || saving) return;
    setSaving(true);
    try {
      const { error } = await supabase.from('imovel_context').insert({
        imovel_id: imovelId,
        autor_tipo: 'human',
        autor_id: user?.email || user?.id || null,
        tipo: 'note',
        texto: texto.trim(),
      });
      if (error) throw error;
      setTexto('');
      await load();
    } catch (err) {
      // eslint-disable-next-line no-alert
      alert(`Erro ao salvar: ${err.message}`);
    } finally {
      setSaving(false);
    }
  };

  if (rows === null) return null;
  return (
    <section>
      <div className="text-[10px] uppercase tracking-wider font-semibold text-slate-500 mb-2 flex items-center gap-1">
        <MessageSquare size={10} /> Timeline / Notas ({rows.length})
      </div>

      <div className="bg-white border border-slate-200 rounded p-2 mb-2">
        <textarea
          value={texto}
          onChange={(e) => setTexto(e.target.value)}
          placeholder="Adicionar nota sobre este imóvel..."
          rows={2}
          className="w-full text-xs border border-slate-200 rounded p-1.5 resize-none focus:outline-none focus:border-blue-400"
        />
        <div className="flex justify-end mt-1">
          <button
            onClick={submit}
            disabled={!texto.trim() || saving}
            className="px-3 py-1 text-xs bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50"
          >
            {saving ? 'Salvando...' : 'Adicionar nota'}
          </button>
        </div>
      </div>

      {rows.length === 0 ? (
        <div className="text-xs text-slate-400 italic">Nenhuma nota. Adicione a primeira.</div>
      ) : (
        <div className="space-y-1">
          {rows.map((r) => (
            <div key={r.id} className="bg-white border border-slate-200 rounded p-2 text-xs">
              <div className="flex items-center justify-between text-[10px] text-slate-500 mb-0.5">
                <span className="flex items-center gap-1">
                  {r.autor_tipo === 'ai' ? '🤖' : '👤'} {r.autor_id || 'anônimo'}
                  {r.tipo && <span className="ml-1 px-1 bg-slate-100 rounded">{r.tipo}</span>}
                </span>
                <span>{r.created_at ? new Date(r.created_at).toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' }) : ''}</span>
              </div>
              <div className="text-slate-700 whitespace-pre-wrap">{r.texto || <span className="text-slate-400 italic">(sem texto)</span>}</div>
            </div>
          ))}
        </div>
      )}
    </section>
  );
};

/**
 * Onda 4 abr/26 — J4: sócios expandíveis de um proprietário PJ.
 */
const SociosInline = ({ empresaId }) => {
  const [rows, setRows] = useState(null);
  useEffect(() => {
    if (!empresaId) return;
    let cancel = false;
    (async () => {
      const { data } = await supabase
        .from('socios')
        .select('*, contato:contatos!socios_socio_contato_id_fkey(first_name, last_name), empresa_socia:empresas!socios_socio_empresa_id_fkey(empresa, company_name)')
        .eq('empresa_id', empresaId)
        .eq('is_active', true)
        .order('percentual_participacao', { ascending: false });
      if (!cancel) setRows(data || []);
    })();
    return () => { cancel = true; };
  }, [empresaId]);
  if (rows === null) return <div className="text-[11px] text-slate-400 italic pl-4">Carregando sócios...</div>;
  if (rows.length === 0) return <div className="text-[11px] text-slate-400 italic pl-4">Nenhum sócio ativo cadastrado.</div>;
  return (
    <div className="pl-4 pt-1 space-y-0.5">
      {rows.map((s) => {
        const nome = s.contato
          ? [s.contato.first_name, s.contato.last_name].filter(Boolean).join(' ')
          : (s.empresa_socia?.empresa || s.empresa_socia?.company_name || '—');
        const isPF = !!s.contato;
        return (
          <div key={s.id} className="text-[11px] text-slate-600 flex items-center gap-1">
            <span className="flex-shrink-0">{isPF ? '👤' : '🏢'}</span>
            <span className="truncate">{nome}</span>
            {s.percentual_participacao != null && (
              <span className="text-slate-400 ml-auto tabular-nums">{Number(s.percentual_participacao).toLocaleString('pt-BR', { maximumFractionDigits: 1 })}%</span>
            )}
            {s.tipo_socio && <span className="text-[10px] px-1 bg-slate-100 rounded">{s.tipo_socio}</span>}
          </div>
        );
      })}
    </div>
  );
};

/**
 * Linha editavel de 1 proprietario (Frente C cascata_por_oferta_proprietarios).
 *
 * 06/mai/26 — Consome v_proprietarios (estrutura flat). Destaque visual conforme
 * `ownership_status`:
 *   - 'deceased' → fundo vermelho suave + borda esquerda + badge "⚠️ Falecido(a)"
 *   - 'heir'     → fundo verde suave + borda esquerda + badge "Herdeiro(a)"
 *   - 'original' → estilo padrão (sem badge)
 * Tooltip dos badges mostra `notas` truncado.
 */
const ProprietarioRow = ({ proprietario, onUpdate, onDelete }) => {
  const p = proprietario;
  const isPJ = p.tipo_pessoa === 'PJ' || !!p.empresa_id;
  const [sociosOpen, setSociosOpen] = useState(false);
  const nome = p.nome_display
    || p.company_name
    || [p.first_name, p.last_name].filter(Boolean).join(' ')
    || '—';
  const docRaw = isPJ ? p.cnpj : p.cpf;
  const tipoLabel = TIPO_PROPRIEDADE_OPTIONS.find(o => o.value === p.tipo_propriedade)?.label
    || p.tipo_propriedade || '—';

  const status = p.ownership_status;
  const isDeceased = status === 'deceased';
  const isHeir = status === 'heir';

  const containerClass = isDeceased
    ? 'bg-red-50 border border-slate-200 border-l-4 border-l-red-400 rounded p-2 text-xs'
    : isHeir
    ? 'bg-emerald-50 border border-slate-200 border-l-4 border-l-emerald-400 rounded p-2 text-xs'
    : 'bg-white border border-slate-200 rounded p-2 text-xs';
  const nameClass = isDeceased
    ? 'col-span-4 font-medium text-gray-500 truncate flex items-center gap-1'
    : 'col-span-4 font-medium text-slate-800 truncate flex items-center gap-1';
  const notasTooltip = (p.notas || '').slice(0, 200);

  return (
    <div className={containerClass}>
    <div className="grid grid-cols-12 gap-2 items-center">
      <div className={nameClass} title={nome}>
        <span className="flex-shrink-0">{isPJ ? '🏢' : '👤'}</span>
        {isPJ && p.empresa_id ? (
          <EntityLink entityType="empresa" entityId={p.empresa_id} label={nome} showIcon={false} className="truncate" />
        ) : !isPJ && p.contato_id ? (
          <EntityLink entityType="contato" entityId={p.contato_id} label={nome} showIcon={false} className="truncate" />
        ) : (
          <span className="truncate">{nome}</span>
        )}
        {docRaw && (
          <span className="text-[10px] font-mono text-slate-400 ml-1 flex-shrink-0 whitespace-nowrap">
            {formatDocument(docRaw)}
          </span>
        )}
        {isDeceased && (
          <span
            className="text-[10px] font-medium px-2 py-0.5 rounded-full bg-red-100 text-red-700 flex-shrink-0 whitespace-nowrap"
            title={notasTooltip || 'Proprietário(a) falecido(a)'}
          >
            ⚠️ Falecido(a)
          </span>
        )}
        {isHeir && (
          <span
            className="text-[10px] font-medium px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700 flex-shrink-0 whitespace-nowrap"
            title={notasTooltip || 'Herdeiro(a)'}
          >
            Herdeiro(a)
          </span>
        )}
        {isPJ && p.empresa_id && (
          <button
            onClick={(e) => { e.stopPropagation(); setSociosOpen((v) => !v); }}
            className="ml-1 text-[10px] px-1 py-0.5 bg-indigo-50 text-indigo-700 rounded hover:bg-indigo-100 flex items-center gap-0.5 flex-shrink-0"
            title={sociosOpen ? 'Ocultar sócios' : 'Ver sócios desta empresa'}
          >
            {sociosOpen ? <ChevronDown size={9} /> : <ChevronRight size={9} />}
            <Users size={9} /> sócios
          </button>
        )}
      </div>
      <div className="col-span-2">
        <EditableCell
          value={p.percentual_participacao != null ? String(p.percentual_participacao) : ''}
          onSave={(v) => {
            const n = parseFloat((v || '').replace(',', '.'));
            if (!isFinite(n) || n <= 0 || n > 100) return;
            onUpdate('percentual_participacao', n);
          }}
          type="text"
          displayValue={p.percentual_participacao != null ? `${p.percentual_participacao}%` : '—'}
          className="text-xs"
        />
      </div>
      <div className="col-span-3">
        <EditableCell
          value={p.tipo_propriedade || 'full_ownership'}
          options={TIPO_PROPRIEDADE_OPTIONS}
          onSave={(v) => onUpdate('tipo_propriedade', v)}
          type="select"
          displayValue={tipoLabel}
          className="text-xs"
        />
      </div>
      <div className="col-span-2">
        <EditableCell
          value={p.data_aquisicao || ''}
          onSave={(v) => onUpdate('data_aquisicao', v || null)}
          type="date"
          displayValue={p.data_aquisicao
            ? new Date(p.data_aquisicao).toLocaleDateString('pt-BR')
            : '—'}
          className="text-xs"
        />
      </div>
      <div className="col-span-1 text-right">
        <button
          onClick={onDelete}
          className="p-0.5 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded"
          title="Remover proprietário"
        >
          <Trash2 size={11} />
        </button>
      </div>
    </div>
    {sociosOpen && isPJ && p.empresa_id && (
      <SociosInline empresaId={p.empresa_id} />
    )}
    </div>
  );
};

/**
 * Form inline de adicao de proprietario — toggle PJ (empresas) / PF (contatos)
 * + search com debounce 300ms.
 *
 * Ao selecionar um resultado, monta payload respeitando o CHECK XOR
 * (proprietario_empresa_id XOR proprietario_contato_id) e chama onSave.
 */
const AddProprietarioForm = ({ onSave, onCancel }) => {
  const [tipo, setTipo] = useState('pj'); // 'pj' | 'pf'
  const [search, setSearch] = useState('');
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [percentual, setPercentual] = useState(100);
  const [tipoPropriedade, setTipoPropriedade] = useState('full_ownership');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const q = search.trim();
    if (q.length < 2) { setResults([]); return; }
    let cancelled = false;
    setSearching(true);
    const timer = setTimeout(async () => {
      try {
        if (tipo === 'pj') {
          const { data } = await supabase
            .from('empresas')
            .select('id, empresa, company_name, papel_negocio')
            .or(`empresa.ilike.%${q}%,company_name.ilike.%${q}%`)
            .limit(8);
          if (!cancelled) setResults(data || []);
        } else {
          const { data } = await supabase
            .from('contatos')
            .select('id, first_name, last_name, email')
            .or(`first_name.ilike.%${q}%,last_name.ilike.%${q}%,email.ilike.%${q}%`)
            .limit(8);
          if (!cancelled) setResults(data || []);
        }
      } finally {
        if (!cancelled) setSearching(false);
      }
    }, 300);
    return () => { cancelled = true; clearTimeout(timer); };
  }, [search, tipo]);

  const handleSelect = async (item) => {
    setSaving(true);
    try {
      const payload = {
        percentual_participacao: percentual,
        tipo_propriedade: tipoPropriedade,
      };
      if (tipo === 'pj') {
        payload.proprietario_empresa_id = item.id;
      } else {
        payload.proprietario_contato_id = item.id;
      }
      await onSave(payload);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="bg-indigo-50 border border-indigo-200 rounded p-2 space-y-2">
      <div className="flex gap-2 items-center flex-wrap">
        <button
          onClick={() => { setTipo('pj'); setResults([]); setSearch(''); }}
          className={`text-[11px] px-2 py-0.5 rounded ${tipo === 'pj' ? 'bg-indigo-600 text-white' : 'bg-white text-slate-600 hover:bg-slate-50'}`}
        >
          🏢 Empresa (PJ)
        </button>
        <button
          onClick={() => { setTipo('pf'); setResults([]); setSearch(''); }}
          className={`text-[11px] px-2 py-0.5 rounded ${tipo === 'pf' ? 'bg-indigo-600 text-white' : 'bg-white text-slate-600 hover:bg-slate-50'}`}
        >
          👤 Pessoa (PF)
        </button>
        <div className="flex items-center gap-1 ml-2">
          <span className="text-[11px] text-slate-600">%:</span>
          <input
            type="number"
            value={percentual}
            onChange={(e) => {
              const n = parseFloat(e.target.value);
              if (isFinite(n) && n > 0 && n <= 100) setPercentual(n);
              else if (e.target.value === '') setPercentual('');
            }}
            className="w-16 text-xs border border-slate-300 rounded px-1 py-0.5"
            min="1" max="100" step="1"
          />
        </div>
        <div className="flex items-center gap-1">
          <span className="text-[11px] text-slate-600">Tipo:</span>
          <select
            value={tipoPropriedade}
            onChange={(e) => setTipoPropriedade(e.target.value)}
            className="text-[11px] border border-slate-300 rounded px-1 py-0.5 bg-white"
          >
            {TIPO_PROPRIEDADE_OPTIONS.map(o => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </select>
        </div>
      </div>

      <input
        type="text"
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        placeholder={tipo === 'pj' ? 'Buscar empresa (mín 2 caracteres)...' : 'Buscar contato...'}
        className="w-full text-xs border border-slate-300 rounded px-2 py-1 focus:ring-2 focus:ring-indigo-400 outline-none"
        autoFocus
        disabled={saving}
      />

      {searching && (
        <div className="text-[11px] text-slate-500 flex items-center gap-1">
          <Loader2 size={10} className="animate-spin" /> Buscando...
        </div>
      )}

      {!searching && search.trim().length >= 2 && results.length === 0 && (
        <div className="text-[11px] text-slate-400 italic">Nenhum resultado.</div>
      )}

      {results.length > 0 && (
        <div className="space-y-0.5 max-h-40 overflow-y-auto">
          {results.map(r => {
            const label = tipo === 'pj'
              ? (r.empresa || r.company_name || '—')
              : (`${[r.first_name, r.last_name].filter(Boolean).join(' ')}${r.email ? ' · ' + r.email : ''}`);
            return (
              <button
                key={r.id}
                onClick={() => handleSelect(r)}
                disabled={saving}
                className="w-full text-left text-xs px-2 py-1 hover:bg-indigo-100 rounded flex items-center gap-1.5 disabled:opacity-50"
              >
                <span>{tipo === 'pj' ? '🏢' : '👤'}</span>
                <span className="truncate">{label}</span>
                {tipo === 'pj' && r.papel_negocio && (
                  <span className="text-[10px] text-slate-400 ml-auto">{r.papel_negocio}</span>
                )}
              </button>
            );
          })}
        </div>
      )}

      <div className="flex justify-end gap-2 pt-1">
        <button
          onClick={onCancel}
          disabled={saving}
          className="text-[11px] text-slate-500 hover:text-slate-700 px-2 py-0.5"
        >
          Cancelar
        </button>
      </div>
    </div>
  );
};

export default ImovelDetailModal;
